/**
 * Retold-Provider-AuthClient
 *
 * The SHARED browser-side auth client. It talks to the routes
 * orator-authentication registers on the server chassis, so an app gets session
 * handling from the foundation rather than writing its own:
 *
 *   GET  /1.0/CheckSession    -> { LoggedIn: bool, ... }
 *   POST /1.0/Authenticate    -> { LoggedIn: bool, ... } (+ sets the session cookie)
 *   GET  /1.0/Deauthenticate  -> ends the session
 *
 * When the comms subsystem has two-step login on (the manifest reports it, and the app
 * sets TwoStepLogin here), sign-in goes through the two-step routes instead:
 *
 *   POST /1.0/Login           -> { LoggedIn } | { Challenge, ChallengeToken, MaskedDestination }
 *   POST /1.0/Login/Verify    -> { LoggedIn } (+ sets the session cookie)
 *   POST /1.0/Login/Resend    -> { Resent }
 *
 * The session lands on AppData.Session so views can read it; a pending challenge lands on
 * AppData.PendingChallenge between the two steps.
 */

const libPictProvider = require('pict').ServiceProviderBase;

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'Retold-AuthClient',
	Dependencies: [],
	RoutePrefix: '/1.0/',
	// When true, login() uses the two-step /Login flow; false keeps /Authenticate.
	TwoStepLogin: false
};

class RetoldProviderAuthClient extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);
		this.routePrefix = this.options.RoutePrefix;
	}

	_url(pPath)
	{
		let tmpPrefix = this.routePrefix;
		if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') { tmpPrefix += '/'; }
		return tmpPrefix + String(pPath).replace(/^\//, '');
	}

	_fetchJSON(pMethod, pURL, pBody)
	{
		let tmpOptions =
		{
			method: pMethod,
			credentials: 'include',
			headers: { 'Accept': 'application/json' }
		};
		if (pBody !== undefined)
		{
			tmpOptions.headers['Content-Type'] = 'application/json';
			tmpOptions.body = JSON.stringify(pBody);
		}
		return fetch(pURL, tmpOptions).then((pResponse) =>
			pResponse.text().then((pText) => (pText ? JSON.parse(pText) : null)));
	}

	session()
	{
		if (!this.fable.AppData.Session) { this.fable.AppData.Session = { LoggedIn: false }; }
		return this.fable.AppData.Session;
	}

	checkSession()
	{
		return this._fetchJSON('GET', this._url('CheckSession'))
			.then((pBody) =>
			{
				let tmpSession = pBody || { LoggedIn: false };
				this.fable.AppData.Session = tmpSession;
				return tmpSession;
			})
			.catch(() =>
			{
				this.fable.AppData.Session = { LoggedIn: false };
				return this.fable.AppData.Session;
			});
	}

	login(pUserName, pPassword)
	{
		// Two-step: POST /Login. It either signs in directly (no 2FA) or returns a challenge
		// the caller completes with verifyLoginCode(). A challenge is stashed on AppData so
		// the verify step needs only the code.
		if (this.options.TwoStepLogin)
		{
			return this._fetchJSON('POST', this._url('Login'), { UserName: pUserName, Password: pPassword })
				.then((pBody) =>
				{
					let tmpResult = pBody || { LoggedIn: false };
					if (tmpResult.Challenge)
					{
						this.fable.AppData.PendingChallenge = { ChallengeToken: tmpResult.ChallengeToken, Channel: tmpResult.Channel, MaskedDestination: tmpResult.MaskedDestination };
						return tmpResult;
					}
					this.fable.AppData.Session = tmpResult;
					return tmpResult;
				})
				.catch(() => ({ LoggedIn: false, Error: 'Sign in failed.' }));
		}
		return this._fetchJSON('POST', this._url('Authenticate'), { UserName: pUserName, Password: pPassword })
			.then((pBody) =>
			{
				let tmpSession = pBody || { LoggedIn: false };
				this.fable.AppData.Session = tmpSession;
				return tmpSession;
			})
			.catch(() => ({ LoggedIn: false, Error: 'Sign in failed.' }));
	}

	// The challenge in flight between step one and step two, if any.
	pendingChallenge() { return this.fable.AppData.PendingChallenge || null; }

	// Complete a two-step sign-in with the code the server sent. On success the session
	// cookie is set and the pending challenge is cleared.
	verifyLoginCode(pCode)
	{
		let tmpPending = this.pendingChallenge() || {};
		return this._fetchJSON('POST', this._url('Login/Verify'), { ChallengeToken: tmpPending.ChallengeToken, Code: pCode })
			.then((pBody) =>
			{
				let tmpResult = pBody || { LoggedIn: false };
				if (tmpResult.LoggedIn)
				{
					this.fable.AppData.Session = tmpResult;
					this.fable.AppData.PendingChallenge = null;
				}
				return tmpResult;
			})
			.catch(() => ({ LoggedIn: false, Error: 'Verification failed.' }));
	}

	// Reissue the two-step code on the pending challenge.
	resendLoginCode()
	{
		let tmpPending = this.pendingChallenge() || {};
		return this._fetchJSON('POST', this._url('Login/Resend'), { ChallengeToken: tmpPending.ChallengeToken })
			.catch(() => ({ Resent: false }));
	}

	// Drop a pending challenge (the user backed out of the code step).
	cancelChallenge() { this.fable.AppData.PendingChallenge = null; }

	// --------------------------------------------------------------------------
	// First-run setup (appliance install): one-time system-administrator bootstrap.
	// --------------------------------------------------------------------------

	// Whether the server still needs its first-run setup (no account yet). Unauthenticated;
	// the boot flow uses it (or the manifest's SetupRequired) to show the setup view.
	//   GET /1.0/Setup/Status -> { SetupRequired, OpenSignup, FirstRunAdmin }
	setupStatus()
	{
		return this._fetchJSON('GET', this._url('Setup/Status'))
			.then((pBody) => pBody || { SetupRequired: false })
			.catch(() => ({ SetupRequired: false }));
	}

	// Create the first-run administrator (POST /1.0/Setup), then sign in with the same
	// credentials so the caller lands authenticated in one step. Resolves to
	// { Setup: bool, LoggedIn: bool, ... }. The payload carries the tenant name + admin
	// profile + credentials (CustomerName/DisplayName/Email/LoginID/Password).
	setup(pPayload)
	{
		let tmpPayload = pPayload || {};
		return this._fetchJSON('POST', this._url('Setup'), tmpPayload)
			.then((pBody) =>
			{
				let tmpResult = pBody || {};
				if (!tmpResult.Setup) { return { Setup: false, LoggedIn: false, Error: (tmpResult.Error || 'Setup failed.') }; }
				return this.login(tmpPayload.LoginID || tmpPayload.UserName || '', tmpPayload.Password || '')
					.then((pSession) => Object.assign({ Setup: true }, pSession || {}));
			})
			.catch(() => ({ Setup: false, LoggedIn: false, Error: 'Setup failed.' }));
	}

	logout()
	{
		return this._fetchJSON('GET', this._url('Deauthenticate'))
			.catch(() => null)
			.then((pBody) =>
			{
				this.fable.AppData.Session = { LoggedIn: false };
				return pBody;
			});
	}
}

module.exports = RetoldProviderAuthClient;
module.exports.default_configuration = _DefaultProviderConfiguration;
